/**
 * Trust AI systems hook (v1.1-A4 / Phase B2) — fetches real
 * ai_systems_inventory rows and adapts them into the mock UI's
 * AiSystem VM shape. Existing /trust/ai-systems page renders without
 * any JSX changes.
 *
 * Fields not covered by A4 (model card, tool grants, knowledge
 * folders, lineage, bias test history) default to sensible empties.
 * Rolled-up metrics (hallucinationRate30d, faithfulnessScore,
 * injection counts) are pulled from the metadata JSONB when present.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api-client';
import type {
  AiSystem,
  AiSystemType,
  AiSystemStatus,
  AiRiskTier,
  AiAutonomyLevel,
} from './mock-trust-data';

// ─── Server VM (mirrors governance-ai-systems.ts SystemSchema) ───

export interface ServerAiSystem {
  id: string;
  systemKind:
    | 'agent'
    | 'model'
    | 'rag_pipeline'
    | 'embedding_model'
    | 'reranker'
    | 'mcp_install'
    | 'workflow_definition'
    | 'custom_function';
  sourceTable: string;
  sourceId: string;
  displayName: string;
  description: string | null;
  status: 'in_development' | 'in_production' | 'deprecated' | 'retired';
  riskClassification:
    | 'minimal'
    | 'limited'
    | 'high'
    | 'unacceptable'
    | null;
  useCaseSummary: string | null;
  dataClassifications: string[];
  decisionAuthority:
    | 'human'
    | 'ai_recommend'
    | 'ai_decide_with_review'
    | 'ai_decide'
    | null;
  humanOversight: 'continuous' | 'sample' | 'on_demand' | 'none' | null;
  ownerUserId: string | null;
  ownerTeam: string | null;
  deployedAt: string | null;
  retiredAt: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

interface ListResponse {
  systems: ServerAiSystem[];
}

// ─── Adapter ─────────────────────────────────────────────────────

const KIND_TO_VM_TYPE: Record<ServerAiSystem['systemKind'], AiSystemType> = {
  agent: 'agent',
  model: 'model',
  rag_pipeline: 'pipeline',
  embedding_model: 'model',
  reranker: 'tool',
  mcp_install: 'tool',
  workflow_definition: 'pipeline',
  custom_function: 'tool',
};

const OVERSIGHT_TO_AUTONOMY: Record<
  NonNullable<ServerAiSystem['humanOversight']>,
  AiAutonomyLevel
> = {
  continuous: 'human_in_loop',
  sample: 'human_in_loop',
  on_demand: 'human_on_loop',
  none: 'human_out_of_loop',
};

function pickNum(meta: Record<string, unknown>, key: string): number | null {
  const v = meta[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function pickInt(meta: Record<string, unknown>, key: string): number {
  const v = meta[key];
  return typeof v === 'number' && Number.isFinite(v) ? Math.trunc(v) : 0;
}

export function adaptServerAiSystem(s: ServerAiSystem): AiSystem {
  const m = s.metadata ?? {};
  const status: AiSystemStatus = s.status; // exact 4 overlap
  const riskTier: AiRiskTier = s.riskClassification ?? 'limited';
  const autonomyLevel: AiAutonomyLevel = s.humanOversight
    ? OVERSIGHT_TO_AUTONOMY[s.humanOversight]
    : 'human_in_loop';

  return {
    id: s.id,
    linkedAgentId: s.sourceTable === 'custom_agents' ? s.sourceId : null,
    name: s.displayName,
    description: s.description ?? '',
    type: KIND_TO_VM_TYPE[s.systemKind],
    status,
    riskTier,
    autonomyLevel,
    intendedPurpose: s.useCaseSummary ?? '',
    ownerUserId: s.ownerUserId ?? '',
    ownerName: '—',
    ownerTeam: s.ownerTeam ?? '—',
    baseModel: typeof m.baseModel === 'string' ? (m.baseModel as string) : '—',
    fineTuningAdapterId:
      typeof m.fineTuningAdapterId === 'string'
        ? (m.fineTuningAdapterId as string)
        : null,
    toolGrants: [],
    knowledgeFolders: [],
    dataInputs: [],
    dataOutputs: [],
    upstreamDependencies: [],
    downstreamConsumers: [],
    modelCard: null,
    conformityAssessmentDate: null,
    lastBiasTestDate: null,
    biasTestPassRate: pickNum(m, 'biasTestPassRate'),
    hallucinationRate30d: pickNum(m, 'hallucinationRate30d'),
    injectionAttempts30d: pickInt(m, 'injectionAttempts30d'),
    injectionsBlocked30d: pickInt(m, 'injectionsBlocked30d'),
    faithfulnessScore: pickNum(m, 'faithfulnessScore'),
    approvalGatesCount: pickInt(m, 'approvalGatesCount'),
    classificationsHandled: s.dataClassifications,
    deployedDate: s.deployedAt ?? s.createdAt,
    retirementDate: s.retiredAt,
    monitoringEnabled: true,
    humanOversightDocUrl: null,
    stopButtonProcedure: '—',
    retrainingCadence: 'never',
    frameworkRelevance: [],
    iconColor: 'blue',
  };
}

// ─── Hook ────────────────────────────────────────────────────────

export function useTrustAiSystems() {
  return useQuery<ListResponse, Error, AiSystem[]>({
    queryKey: ['trust', 'ai-systems'],
    queryFn: () => apiClient.get<ListResponse>('/governance/ai-systems'),
    select: (res) => res.systems.map(adaptServerAiSystem),
  });
}

// ─── Register AI System (Trust #11) ──────────────────────────────
// The "Register AI System" wizard posts here. Mirrors the POST
// /governance/ai-systems body (governance-ai-systems.ts:215).
export type AiSystemKind = ServerAiSystem['systemKind'];

export interface RegisterAiSystemInput {
  systemKind: AiSystemKind;
  sourceTable: string;
  sourceId: string;
  displayName: string;
  description?: string | null;
  status?: ServerAiSystem['status'];
  riskClassification?: ServerAiSystem['riskClassification'];
  useCaseSummary?: string | null;
  dataClassifications?: string[];
  decisionAuthority?: ServerAiSystem['decisionAuthority'];
  humanOversight?: ServerAiSystem['humanOversight'];
  ownerUserId?: string | null;
  ownerTeam?: string | null;
  metadata?: Record<string, unknown>;
}

export function useRegisterAiSystem() {
  const qc = useQueryClient();
  return useMutation<ServerAiSystem, Error, RegisterAiSystemInput>({
    mutationFn: (body) =>
      apiClient.post<ServerAiSystem>('/governance/ai-systems', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['trust', 'ai-systems'] });
    },
  });
}
